import { baseApi } from "./baseApi";

/**
 * Pins and drafts (V192) — the two chat-panel features.
 *
 * One slice because they share every surface: the chat panel raises both, and a
 * prospect's record shows both. They are GATED SEPARATELY though, so each family
 * of endpoints answers its own `{enabled:false}` and a tenant may have one
 * without the other.
 *
 * ## Pins carry TWO facts about the person, and only one of them is a name
 *
 * `personVisible` is not a convenience flag. A pin is readable by whoever can see
 * the THREAD, which is not the same set as whoever can see the PROSPECT — so the
 * server withholds the name when the subject is outside the reader's communities
 * and hands back `personVisible: false`. The row is still returned, because the
 * person who pinned it should know the thread is attached to something rather
 * than pin it again; what they must not learn is who.
 *
 * Rendering `personName` without checking `personVisible` would print "null" or
 * an empty chip where a name belongs. Rendering the row as unattached would be
 * worse — it invites a duplicate pin.
 *
 * ## A DRAFT IS NOT A SENT MESSAGE
 *
 * There is no status field on `AgentDraft` and no endpoint here that sends one,
 * because there is no send path from a draft at all. `personId` means FILED
 * UNDER — it is not a recipient. Any UI built on this slice has to keep that
 * unambiguous; it is the one confusion in this feature that would do real harm.
 */

// ---------------------------------------------------------------------------
// Pins
// ---------------------------------------------------------------------------

export interface SessionPin {
  id: string;
  sessionId: string;
  personId: string;
  /** Null when this reader may not see the prospect. Check `personVisible`. */
  personName: string | null;
  personVisible: boolean;
  pinnedBy: string;
  pinnedAt: string;
}

/** A thread pinned to a prospect, as their record shows it. */
export interface PinnedThread {
  pinId: string;
  sessionId: string;
  title: string | null;
  lastActivityAt: string;
  pinnedBy: string;
  pinnedAt: string;
}

// ---------------------------------------------------------------------------
// Drafts
// ---------------------------------------------------------------------------

/** A shape of text, never a delivery state. There is no `sent` and never will be. */
export type DraftKind = "email" | "message" | "note" | "document";

export interface AgentDraft {
  id: string;
  kind: DraftKind;
  title: string;
  body: string;
  /** Where it came from; null once that thread has been swept. */
  sessionId: string | null;
  /** FILED UNDER. Not a recipient. */
  personId: string | null;
  /** Null when filed under someone this reader cannot see. */
  personName: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export const crmAgentPinsDraftsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── Pins ──────────────────────────────────────────────────────────────
    getSessionPins: builder.query<
      { enabled?: boolean; pins: SessionPin[] },
      { instanceId: string; sessionId: string }
    >({
      query: ({ instanceId, sessionId }) => ({
        url: `/crm/skyagent/sessions/${sessionId}/pins`,
        params: { instanceId },
      }),
      providesTags: ["CrmAgentPins"],
    }),

    pinSessionToPerson: builder.mutation<
      { pin: SessionPin },
      { instanceId: string; sessionId: string; personId: string }
    >({
      query: ({ instanceId, sessionId, personId }) => ({
        url: `/crm/skyagent/sessions/${sessionId}/pins`,
        method: "POST",
        params: { instanceId },
        body: { personId },
      }),
      invalidatesTags: ["CrmAgentPins"],
    }),

    unpinSession: builder.mutation<{ id: string }, { instanceId: string; pinId: string }>({
      query: ({ instanceId, pinId }) => ({
        url: `/crm/skyagent/pins/${pinId}`,
        method: "DELETE",
        params: { instanceId },
      }),
      invalidatesTags: ["CrmAgentPins"],
    }),

    /** The threads pinned to one prospect — their record's view. */
    getPersonThreads: builder.query<
      { enabled?: boolean; threads: PinnedThread[] },
      { instanceId: string; personId: string }
    >({
      query: ({ instanceId, personId }) => ({
        url: `/crm/persons/${personId}/agent-threads`,
        params: { instanceId },
      }),
      providesTags: ["CrmAgentPins"],
    }),

    // ── Drafts ────────────────────────────────────────────────────────────
    getDrafts: builder.query<
      { enabled?: boolean; drafts: AgentDraft[]; total: number },
      {
        instanceId: string;
        q?: string;
        mine?: boolean;
        personId?: string;
        limit?: number;
        offset?: number;
      }
    >({
      query: ({ instanceId, q, mine, personId, limit, offset }) => ({
        url: "/crm/skyagent/drafts",
        params: {
          instanceId,
          ...(q ? { q } : {}),
          // ALWAYS sent, both values. The route reads `mine !== "false"`, so an
          // OMITTED parameter means "mine only" — omitting it when the caller
          // switched the toggle OFF asked for everything and was answered with
          // their own drafts, leaving a shipped control doing nothing. The
          // community ceiling is enforced in the store either way, so this
          // decides a preference and never an access boundary.
          ...(mine === undefined ? {} : { mine: mine ? "true" : "false" }),
          ...(personId ? { personId } : {}),
          ...(limit != null ? { limit } : {}),
          ...(offset != null ? { offset } : {}),
        },
      }),
      providesTags: ["CrmAgentDrafts"],
    }),

    /** The drafts filed under one prospect — their record's view. */
    getPersonDrafts: builder.query<
      { enabled?: boolean; drafts: AgentDraft[] },
      { instanceId: string; personId: string }
    >({
      query: ({ instanceId, personId }) => ({
        url: `/crm/persons/${personId}/agent-drafts`,
        params: { instanceId },
      }),
      providesTags: ["CrmAgentDrafts"],
    }),

    createDraft: builder.mutation<
      { draft: AgentDraft },
      {
        instanceId: string;
        kind: DraftKind;
        title: string;
        body: string;
        sessionId?: string | null;
        personId?: string | null;
      }
    >({
      query: ({ instanceId, ...body }) => ({
        url: "/crm/skyagent/drafts",
        method: "POST",
        params: { instanceId },
        body,
      }),
      invalidatesTags: ["CrmAgentDrafts"],
    }),

    updateDraft: builder.mutation<
      { draft: AgentDraft },
      {
        instanceId: string;
        id: string;
        title?: string;
        body?: string;
        /** `null` DETACHES from the prospect; omit to leave the filing alone. */
        personId?: string | null;
      }
    >({
      query: ({ instanceId, id, ...body }) => ({
        url: `/crm/skyagent/drafts/${id}`,
        method: "PATCH",
        params: { instanceId },
        body,
      }),
      invalidatesTags: ["CrmAgentDrafts"],
    }),

    deleteDraft: builder.mutation<{ id: string }, { instanceId: string; id: string }>({
      query: ({ instanceId, id }) => ({
        url: `/crm/skyagent/drafts/${id}`,
        method: "DELETE",
        params: { instanceId },
      }),
      invalidatesTags: ["CrmAgentDrafts"],
    }),
  }),
});

export const {
  useGetSessionPinsQuery,
  usePinSessionToPersonMutation,
  useUnpinSessionMutation,
  useGetPersonThreadsQuery,
  useGetDraftsQuery,
  useGetPersonDraftsQuery,
  useCreateDraftMutation,
  useUpdateDraftMutation,
  useDeleteDraftMutation,
} = crmAgentPinsDraftsApi;
